import { createFileRoute } from '@tanstack/react-router'

import { useState, useEffect } from "react";
import { useSession, signOut } from '@/lib/mocks/session';
import { useRouter } from '@/lib/mocks/router';
import { demoActions, useDemoState } from '@/lib/demo-store';
import { features } from '@/lib/platform';
import { paso } from '@/modules/demo/DemoTour';
import { NotificationsProvider } from '@/modules/notifications/NotificationsProvider';
import { NotificationBell } from '@/modules/notifications/NotificationBell';
import { motion, AnimatePresence } from "framer-motion";
import { SignOutButton } from '@/lib/mocks/cdp';
import {
  TrendingUp,
  Clock,
  LogOut,
  User,
  Mail,
  Calendar,
  Shield,
  PieChart,
  Settings,
  HelpCircle,
  Building2,
  Wallet,
  ArrowUpRight,
  ArrowDownRight,
  Search,
  ChevronRight,
  MapPin,
  Users,
  Star,
  ExternalLink,
  Download,
  CreditCard,
  History,
  Home,
  TrendingDown,
  DollarSign,
  Eye,
  Filter,
  Grid,
  List,
  RefreshCw,
  Sparkles,
  Target,
  Zap,
  BarChart3,
  Activity,
  Award,
  CheckCircle2,
  CircleDollarSign,
  Landmark,
  LineChart,
  Copy,
  Check,
  Loader2,
} from "lucide-react";
import { Button } from '@platform/ui';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@platform/ui';
import { LanguageSwitcher } from '@/modules/shared/LanguageSwitcher';
import { Switch } from '@platform/ui';
import { Label } from '@platform/ui';
import { Badge } from '@platform/ui';
import { Avatar, AvatarFallback, AvatarImage } from '@platform/ui';
import { Input } from '@platform/ui';
import { Progress } from '@platform/ui';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@platform/ui';
import { Separator } from '@platform/ui';
import { Skeleton } from '@platform/ui';
import WalletCard from '@/modules/wallet/WalletCard';
import { PortfolioValuation } from '@/modules/wallet/PortfolioValuation';
import { PropertyHoldingCard } from '@/modules/wallet/PropertyHoldingCard';
import { WalletYieldSummary } from '@/modules/wallet/WalletYieldSummary';
import { ProfileHero } from '@/modules/profile/ProfileHero';
import { ProfileCompletion } from '@/modules/profile/ProfileCompletion';
import { AccountDetails } from '@/modules/profile/AccountDetails';
import { ProfileAchievements } from '@/modules/profile/ProfileAchievements';
import { EditProfileModal } from '@/modules/profile/EditProfileModal';
import { SecurityQuestionsCard } from '@/modules/account-security/SecurityQuestionsCard';
import {
  SidebarProvider,
  SidebarTrigger,
  Sidebar,
  SidebarHeader,
  SidebarSeparator,
  SidebarContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarFooter,
  SidebarInset,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenuBadge,
  SidebarRail,
} from '@platform/ui';
import { useProjects, ProjectListItem } from '@/lib/mocks/useProjects';
import { useTranslations } from '@/i18n/useTranslations';
import { BrandLogo } from '@/modules/shared/BrandLogo';
import { HelpSection } from '@/modules/help/HelpSection';
import { brand } from '@/lib/platform';
import KycVerification from '@/modules/kyc/KycVerification';
import { OnboardingRequiredModal } from '@/modules/onboarding/OnboardingRequiredModal';
import { OnboardingBanner } from '@/modules/onboarding/OnboardingBanner';
import { useOnboarding } from '@/lib/onboarding/useOnboarding';
import { DepositModal } from '@/modules/wallet/DepositModal';
import { createPublicClient, http, parseAbi, formatUnits } from 'viem';
import { baseSepolia } from 'viem/chains';
import { useEvmAddress } from '@/lib/mocks/cdp';
import { getNetworkConfig } from "@/lib/contracts-shim";
import {
  mockPortfolio,
  mockTransactions,
  getPortfolioTotals,
} from '@/lib/mocks/portfolio';

// USDC Base Contract
/**
 * Sección de proyectos destacados. Se habilita cuando haya suficientes
 * proyectos en producción. Antes era un `{false && …}` literal en el JSX, que
 * el análisis marca como expresión constante; con una constante con nombre la
 * intención queda explícita y el análisis queda limpio.
 */
const SHOW_FEATURED_PROJECTS = false;

const USDC_ADDRESS = "0x95d833C267810192daEf7B443394c2537E988C5a"; // Custom USDC for Demo
const USDC_ABI = parseAbi([
  'function balanceOf(address owner) view returns (uint256)'
]);

const publicClient = createPublicClient({
  chain: baseSepolia,
  transport: http()
});

// Inversiones del usuario: ver mockPortfolio en @/lib/mocks/portfolio

import { WithdrawalForm } from "@/modules/withdrawals/WithdrawalForm";
import { WithdrawalList } from "@/modules/withdrawals/WithdrawalList";
import { DividendsSection } from "@/modules/dividends/DividendsSection";

function UserDashboard() {
  const { data: session, status, update } = useSession();
  const router = useRouter();
  const { evmAddress } = useEvmAddress(); // Get wallet address from CDP
  // Live demo-store snapshot — in demo mode this drives KYC status, USDC
  // balance and transaction history; in production these reads short-circuit
  // because features.demoMode is false.
  const demoState = useDemoState();

  const [activeSection, setActiveSection] = useState<string>("marketplace");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  // Initialize from the persisted demo-store snapshot so a verified user is
  // already VERIFIED on the first render — no flicker, no false-positive
  // KycRequiredModal when the marketplace mounts.
  const [kycStatus, setKycStatus] = useState<string>(() => {
    if (features.demoMode) {
      try {
        const raw = typeof window !== 'undefined'
          ? window.localStorage.getItem('platform.demo-state.v1')
          : null;
        if (raw) {
          const parsed = JSON.parse(raw);
          return parsed?.user?.kycStatus ?? 'PENDING';
        }
      } catch { /* ignore */ }
    }
    return 'PENDING';
  });
  // Demo mode resolves KYC synchronously from localStorage above, so skip the
  // page-blocking loader. In production we still wait for the backend check.
  const [isCheckingKyc, setIsCheckingKyc] = useState(!features.demoMode);
  const [showKycModal, setShowKycModal] = useState(false);
  // Perfil: flujo KYC desplegado bajo demanda + modal de edición.
  const [showKycFlow, setShowKycFlow] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false); // Deposit Modal State
  const [copied, setCopied] = useState(false);
  const [withdrawalsRefreshKey, setWithdrawalsRefreshKey] = useState(0);

  // New states for real data
  // New states for real data
  const [usdcBalance, setUsdcBalance] = useState<string>("0.00");
  const [transactions, setTransactions] = useState<any[]>([]);
  const [portfolio, setPortfolio] = useState<any[]>([]);
  const [portfolioTotalInvested, setPortfolioTotalInvested] = useState<string>("0");
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
  const [isLoadingPortfolio, setIsLoadingPortfolio] = useState(false);
  const [isLoadingBalance, setIsLoadingBalance] = useState(true);

  const t = useTranslations("Dashboard");
  const te = useTranslations("DashboardExtended");
  const tw = useTranslations("WithdrawalsSection");
  const tCommon = useTranslations("Common");

  // Fetch projects from BigAdmin API
  const { projects: apiProjects, isLoading: isLoadingProjects, error: projectsError } = useProjects({ limit: 20 });
  
  // Projects from BigAdmin API
  const projects: ProjectListItem[] = apiProjects;

  // Log projects for debugging
  useEffect(() => {
    if (projects.length > 0) {
      console.log("🔍 Projects received from API:", projects);
    }
  }, [projects]);

  // USDC Balance — in demo mode it reflects demoStore.balanceUsdc live
  // (updated by the Stripe onramp simulator); otherwise it reads on-chain.
  useEffect(() => {
    if (features.demoMode) {
      setUsdcBalance(demoState.balanceUsdc.toFixed(2));
      setIsLoadingBalance(false);
      return;
    }

    const fetchBalance = async () => {
      if (!evmAddress) {
        setIsLoadingBalance(false);
        return;
      }
      try {
        const balance = await publicClient.readContract({
          address: USDC_ADDRESS,
          abi: USDC_ABI,
          functionName: 'balanceOf',
          args: [evmAddress as `0x${string}`],
        });
        setUsdcBalance(formatUnits(balance, 6)); // USDC has 6 decimals
      } catch (error) {
        console.error("Error fetching USDC balance:", error);
      } finally {
        setIsLoadingBalance(false);
      }
    };

    if (evmAddress) {
      fetchBalance();
      const interval = setInterval(fetchBalance, 30000);
      return () => clearInterval(interval);
    }
  }, [evmAddress, demoState.balanceUsdc]);

  // Fetch Transactions and Portfolio from API
  useEffect(() => {
    const fetchTransactions = async () => {
      if (!evmAddress) {
        setTransactions([]);
        return;
      }
      
      setIsLoadingTransactions(true);
      try {
        const response = await fetch(`/api/transactions?walletAddress=${evmAddress}`);
        if (response.ok) {
          const data = await response.json();
          const txs = data.data || data || []; 
          setTransactions(Array.isArray(txs) ? txs : []);
        }
      } catch (error) {
        console.error("Error fetching transactions:", error);
      } finally {
        setIsLoadingTransactions(false);
      }
    };

    const fetchPortfolio = async () => {
        if (!evmAddress) {
          setPortfolio([]);
          setPortfolioTotalInvested("0");
          return;
        }
        setIsLoadingPortfolio(true);
        try {
            const response = await fetch(`/api/portfolio?walletAddress=${evmAddress}`);
            if (response.ok) {
                const json = await response.json();
                if (json.success && json.data) {
                    const rawPortfolio = json.data.portfolio || [];
                    
                    // Fetch real-time balances from blockchain
                    const enrichedPortfolio = await Promise.all(rawPortfolio.map(async (item: any) => {
                        let realBalance = "0";
                        if (item.rwaContractAddress && evmAddress) {
                            try {
                                const balance = await publicClient.readContract({
                                    address: item.rwaContractAddress as `0x${string}`,
                                    abi: USDC_ABI, // Using USDC_ABI as generic ERC20 ABI for balanceOf
                                    functionName: 'balanceOf',
                                    args: [evmAddress as `0x${string}`],
                                });
                                // Assuming 18 decimals for RWA tokens, change to 6 if they copy USDC
                                realBalance = formatUnits(balance as bigint, 18); 
                            } catch (err) {
                                console.warn(`Failed to fetch balance for ${item.projectSymbol}:`, err);
                                // Fallback logic if needed, or keep 0
                            }
                        }
                        return { ...item, realBalance };
                    }));

                    setPortfolio(enrichedPortfolio);
                    setPortfolioTotalInvested(json.data.totalInvestedUsd || "0");
                }
            }
        } catch (error) {
            console.error("Error fetching portfolio:", error);
        } finally {
            setIsLoadingPortfolio(false);
        }
    };

    if (evmAddress) {
        if (activeSection === "balances" || activeSection === "history") {
            fetchTransactions();
        }
        if (activeSection === "portfolio" || activeSection === "balances") {
            fetchPortfolio();
        }
    }
  }, [evmAddress, activeSection]);

  // Check KYC status — re-runs whenever the session OR the demo-store user
  // changes, so the simulated VERIFIED state propagates here without a reload.
  useEffect(() => {
    if (status === "loading") {
      return;
    }

    // Demo mode: auto-login a placeholder user so the dashboard is reachable
    // without going through the login modal first. Lets the client click a link
    // and land directly inside the product.
    if (!session?.user?.id) {
      if (features.demoMode) {
        demoActions.login('demo@platform.app');
        return;
      }
      router.push("/");
      return;
    }

    checkKycStatus();
  }, [session, status, demoState.user?.kycStatus]);

  const checkKycStatus = async () => {
    try {
      // Demo mode: skip the backend round-trip entirely — the simulator stores
      // the verification result in the demo-store, and useSession() already
      // mirrors it. This keeps the dashboard in sync the moment the user
      // finishes the in-app KYC flow (no /api endpoint, no polling).
      if (features.demoMode) {
        const next = (demoState.user?.kycStatus as string | undefined) ?? 'PENDING';
        setKycStatus(next);
        return;
      }

      const response = await fetch("/api/kyc/check-status");
      if (response.ok) {
        const data = await response.json();
        const newStatus = data.kycStatus || "PENDING";
        setKycStatus(newStatus);

        // Sync session if different to ensure CDP Provider sees the correct status
        if (session?.user && session.user.kycStatus !== newStatus) {
            console.log("🔄 UserDashboard: Updating session KYC status...", newStatus);
            await update({ kycStatus: newStatus });
        }
      }
    } catch (error) {
      console.error("Error checking KYC:", error);
    } finally {
      setIsCheckingKyc(false);
    }
  };

  // Inversiones y rendimientos: datos reales del backend si existen; si no, mocks de demo.
  const displayPortfolio: any[] = portfolio.length > 0 ? portfolio : (features.demoMode ? mockPortfolio : []);
  const displayTransactions: any[] = transactions.length > 0 ? transactions : (features.demoMode ? mockTransactions : []);

  // Totales del portafolio (sobre los mocks cuando no hay datos reales).
  const { totalInvested, totalCurrentValue, totalEarnings, earningsPercent } = getPortfolioTotals(mockPortfolio);

  // Mock for legacy variables
  const pendingWithdrawals = 0;
  const availableBalance = parseFloat(usdcBalance || "0");

  const handleAuthLogout = () => {
    // Limpiar cache de CDP Smart Wallet al cerrar sesión
    if (typeof window !== "undefined") {
      try {
        console.log("👋 HandleAuthLogout: Limpiando caches de CDP Smart Wallet...");
        Object.keys(localStorage).forEach((key) => {
          if (
            key.toLowerCase().includes("coinbase") ||
            key.toLowerCase().includes("walletlink") || 
            key.includes("CBWSDK")
          ) {
            console.log(`🧹 Eliminando key: ${key}`);
            localStorage.removeItem(key);
          }
        });
      } catch (error) {
        console.error("⚠️ Error limpiando cache de CDP en logout:", error);
      }
    }
    signOut({ callbackUrl: "/" });
  };

  const getInitials = (name: string) => {
    return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // El badge se comporta según el estado del proyecto y los flags on-chain de venta
  // (isSaleActive / isWhitelistSaleActive). Venta pausada tiene prioridad visual.
  const getStatusBadge = (project: { status: string; isSaleActive?: boolean; isWhitelistSaleActive?: boolean }) => {
    if (project.status === "active" && project.isSaleActive === false) {
      return project.isWhitelistSaleActive
        ? <Badge className="bg-indigo-500/20 text-indigo-400 border-indigo-500/30 px-3 gap-1"><Clock className="w-3 h-3" /> {te("whitelist_only")}</Badge>
        : <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 px-3 gap-1"><Clock className="w-3 h-3" /> {te("sale_paused")}</Badge>;
    }
    switch (project.status) {
      case "active":
        return <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 px-3 gap-1"><Zap className="w-3 h-3" /> {t("active")}</Badge>;
      case "funded":
        return <Badge className="bg-sky-500/20 text-sky-400 border-sky-500/30 px-3 gap-1"><CheckCircle2 className="w-3 h-3" /> {t("funded")}</Badge>;
      case "coming_soon":
        return <Badge className="bg-violet-500/20 text-violet-400 border-violet-500/30 px-3 gap-1"><Clock className="w-3 h-3" /> {t("coming_soon")}</Badge>;
      default:
        return null;
    }
  };

  const filteredProjects = projects.filter((project) => {
    const matchesSearch = project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.location.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterStatus === "all" || project.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  // Onboarding de 2 tareas (KYC + preguntas de seguridad). El gate exige AMBAS.
  //
  // Va AQUÍ, antes del return temprano de carga. Estaba más abajo, después de
  // ese return, y eso rompe la regla de orden de ganchos de React: en el render
  // de carga no se llegaba a llamarlo y en el siguiente sí, así que React veía
  // un gancho de más y lanzaba en tiempo de ejecución. Lo detectó la puerta de
  // análisis estático de Fase 0.
  const { isComplete: isOnboardingComplete } = useOnboarding();

  if (status === "loading" || isCheckingKyc) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <RefreshCw className="w-10 h-10 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">{t("loading_dashboard")}</p>
        </div>
      </div>
    );
  }

  // Live KYC gate — derive from the demo-store snapshot in demo mode so the
  // marketplace card click never opens the KYC modal on an already-verified
  // user (previously caused an intermittent flash before the local state caught up).
  const isKycVerified = features.demoMode
    ? demoState.user?.kycStatus === 'VERIFIED'
    : kycStatus === 'VERIFIED';

  const handleInvestClick = (projectId: string) => {
    if (!isOnboardingComplete) {
      setShowKycModal(true);
    } else {
      router.push(`/dashboard/user/projects/${projectId}`);
    }
  };

  return (
    <NotificationsProvider userId={session?.user?.id}>
    <SidebarProvider style={{ '--sidebar-width-icon': '2.5rem' } as React.CSSProperties}>
      <Sidebar variant="floating" collapsible="icon" className="p-3 group-data-[collapsible=icon]:!p-1 platform-sidebar">
        <SidebarHeader className="px-3 py-4 group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:py-2">
          <div className="flex items-center justify-between group-data-[collapsible=icon]:justify-center">
            <div className="group-data-[collapsible=icon]:hidden">
              <BrandLogo variant="primary" textClassName="text-lg font-semibold tracking-tight" />
            </div>
            <div className="hidden group-data-[collapsible=icon]:flex h-7 w-7 items-center justify-center rounded-md bg-primary/15 text-primary text-xs font-bold">
              {brand.name.charAt(0).toUpperCase()}
            </div>
          </div>
        </SidebarHeader>
        <SidebarSeparator className="mx-3" />
        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupLabel className="text-xs uppercase tracking-wider text-muted-foreground/60">{t("main")}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu className="gap-1">
                <SidebarMenuItem>
                  <SidebarMenuButton
                    tooltip={t("marketplace")}
                    isActive={activeSection === "marketplace"}
                    onClick={() => setActiveSection("marketplace")}
                    className="h-10"
                  >
                    <Building2 className="h-4 w-4" />
                    <span>{t("marketplace")}</span>
                  </SidebarMenuButton>
                  <SidebarMenuBadge className="bg-primary/20 text-primary">{projects.filter(p => p.status === "active").length}</SidebarMenuBadge>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    tooltip={t("my_investments")}
                    isActive={activeSection === "portfolio"}
                    onClick={() => setActiveSection("portfolio")}
                    className="h-10"
                  >
                    <PieChart className="h-4 w-4" />
                    <span>{t("my_investments")}</span>
                  </SidebarMenuButton>
                  <SidebarMenuBadge>{displayPortfolio.length}</SidebarMenuBadge>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          <SidebarGroup>
            <SidebarGroupLabel className="text-xs uppercase tracking-wider text-muted-foreground/60">{t("finances")}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu className="gap-1">
                <SidebarMenuItem>
                  <SidebarMenuButton
                    tooltip={t("balances")}
                    isActive={activeSection === "balances"}
                    onClick={() => setActiveSection("balances")}
                    className="h-10"
                  >
                    <Wallet className="h-4 w-4" />
                    <span>{t("balances")}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    tooltip={t("withdrawals")}
                    isActive={activeSection === "withdrawals"}
                    onClick={() => setActiveSection("withdrawals")}
                    className="h-10"
                  >
                    <ArrowUpRight className="h-4 w-4" />
                    <span>{t("withdrawals")}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                {features.demoMode && (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      tooltip={t("dividends")}
                      isActive={activeSection === "dividends"}
                      onClick={() => setActiveSection("dividends")}
                      className="h-10"
                    >
                      <CircleDollarSign className="h-4 w-4" />
                      <span>{t("dividends")}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
                <SidebarMenuItem>
                  <SidebarMenuButton
                    tooltip={t("history")}
                    isActive={activeSection === "history"}
                    onClick={() => setActiveSection("history")}
                    className="h-10"
                  >
                    <History className="h-4 w-4" />
                    <span>{t("history")}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          <SidebarGroup>
            <SidebarGroupLabel className="text-xs uppercase tracking-wider text-muted-foreground/60">{t("account")}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu className="gap-1">
                <SidebarMenuItem>
                  <SidebarMenuButton
                    tooltip={t("profile")}
                    isActive={activeSection === "profile"}
                    onClick={() => setActiveSection("profile")}
                    className="h-10"
                  >
                    <User className="h-4 w-4" />
                    <span>{t("profile")}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    tooltip={t("settings")}
                    isActive={activeSection === "settings"}
                    onClick={() => setActiveSection("settings")}
                    className="h-10"
                  >
                    <Settings className="h-4 w-4" />
                    <span>{t("settings")}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    tooltip={t("help")}
                    isActive={activeSection === "help"}
                    onClick={() => setActiveSection("help")}
                    className="h-10"
                  >
                    <HelpCircle className="h-4 w-4" />
                    <span>{t("help")}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
        <SidebarRail />
        <SidebarFooter className="px-3 py-3 group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:py-2">
          <div className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/10 mb-3 group-data-[collapsible=icon]:hidden">
            <Avatar className="h-10 w-10 border-2 border-primary/30 ring-2 ring-primary/10">
              <AvatarFallback className="bg-primary/20 text-primary font-bold">
                {session?.user?.name ? getInitials(session.user.name) : "U"}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">
                {session?.user?.name || tCommon("user")}
              </p>
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                {kycStatus === "VERIFIED" ? (
                  <>
                    <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                    <span>{t("verified")}</span>
                  </>
                ) : (
                  <>
                    <Clock className="w-3 h-3 text-amber-500" />
                    <span>{t("pending_kyc")}</span>
                  </>
                )}
              </div>
            </div>
          </div>
          {/* Collapsed avatar (icon-only mode) */}
          <div className="hidden group-data-[collapsible=icon]:flex justify-center mb-1">
            <Avatar className="h-7 w-7 border border-primary/30">
              <AvatarFallback className="bg-primary/20 text-primary font-bold text-[10px]">
                {session?.user?.name ? getInitials(session.user.name) : "U"}
              </AvatarFallback>
            </Avatar>
          </div>
          {/* Logout button — collapses to small icon */}
          <div className="w-full flex group-data-[collapsible=icon]:justify-center">
            <Button
              variant="outline"
              title={t("logout")}
              className="w-full h-10 border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md text-sm font-medium transition-colors justify-start group-data-[collapsible=icon]:w-7 group-data-[collapsible=icon]:h-7 group-data-[collapsible=icon]:p-0 group-data-[collapsible=icon]:justify-center"
              onClick={handleAuthLogout}
            >
              <LogOut className="w-4 h-4 mr-2 group-data-[collapsible=icon]:w-4 group-data-[collapsible=icon]:h-4 group-data-[collapsible=icon]:mr-0" />
              <span className="group-data-[collapsible=icon]:hidden">{t("logout")}</span>
            </Button>
          </div>
        </SidebarFooter>
      </Sidebar>

      <SidebarInset className="relative bg-background min-w-0">
        {/* Ambient gradient backdrop — fixed to viewport so the SAME gradient lives
            behind the sticky header AND the main content (no seams between them). */}
        <div aria-hidden className="pointer-events-none fixed inset-0 bg-mesh-soft opacity-60" />
        {/* Top Header — transparent + light frost so the shared gradient flows through it. */}
        <header className="bg-transparent px-8 py-6" {...paso('bienvenida')}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <SidebarTrigger className="h-9 w-9 rounded-lg hover:bg-muted transition-colors" />
              <div>
              <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                {activeSection === "marketplace" && <><Building2 className="w-6 h-6 text-primary" /> {t("marketplace")}</>}
                {activeSection === "portfolio" && <><PieChart className="w-6 h-6 text-primary" /> {t("my_investments")}</>}
                {activeSection === "balances" && <><Wallet className="w-6 h-6 text-primary" /> {t("my_balances")}</>}
                {activeSection === "withdrawals" && <><ArrowUpRight className="w-6 h-6 text-primary" /> {t("withdrawals")}</>}
                {activeSection === "dividends" && <><CircleDollarSign className="w-6 h-6 text-primary" /> {t("dividends")}</>}
                {activeSection === "history" && <><History className="w-6 h-6 text-primary" /> {t("history")}</>}
                {activeSection === "profile" && <><User className="w-6 h-6 text-primary" /> {t("my_profile")}</>}
                {activeSection === "settings" && <><Settings className="w-6 h-6 text-primary" /> {t("settings")}</>}
                {activeSection === "help" && <><HelpCircle className="w-6 h-6 text-primary" /> {t("help")}</>}
              </h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                {activeSection === "marketplace" && t("marketplace_desc")}
                {activeSection === "portfolio" && t("my_investments_desc")}
                {activeSection === "balances" && t("balances_desc")}
                {activeSection === "withdrawals" && t("withdrawals_desc")}
                {activeSection === "dividends" && t("dividends_desc")}
                {activeSection === "history" && t("history_desc")}
                {activeSection === "profile" && t("profile_desc")}
                {activeSection === "settings" && t("settings_desc")}
                {activeSection === "help" && t("help_desc")}
              </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <NotificationBell />
              <Avatar className="h-10 w-10 border-2 border-primary/20">
                <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                  {session?.user?.name ? getInitials(session.user.name) : "U"}
                </AvatarFallback>
              </Avatar>
            </div>
          </div>
        </header>

        <main className="p-8">
          <AnimatePresence mode="wait">
            {/* Marketplace Section */}
            {activeSection === "marketplace" && (
              <motion.div
                key="marketplace"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="space-y-8"
                {...paso('catalogo')}
              >
                {/* Welcome Banner */}
                <Card className="relative overflow-hidden border border-border/60 bg-card/70 backdrop-blur-xl shadow-soft-md animate-slide-up-fade">
                  <div aria-hidden className="pointer-events-none absolute inset-0 bg-mesh opacity-90" />
                  <div aria-hidden className="pointer-events-none absolute inset-0 bg-grid-soft opacity-50" />
                  <CardContent className="p-8 relative">
                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                      <div className="space-y-3">
                        <span className="chip-brand">
                          <Sparkles className="w-3.5 h-3.5" />
                          {t("welcome_back")}
                        </span>
                        <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-balance">
                          {t("hello")}, {session?.user?.name?.split(" ")[0] || t("investor")}
                        </h2>
                        <p className="text-muted-foreground max-w-md text-pretty">
                          {t("portfolio_grew")} <span className="text-success font-semibold tabular-nums">+{earningsPercent}%</span> {t("this_month")}.
                          {t("explore_opportunities")}
                        </p>
                      </div>
                      <div className="flex gap-3">
                        <Button className="gap-2 h-11 px-6 rounded-full button-gradient">
                          <Target className="w-4 h-4" />
                          {t("view_opportunities")}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Onboarding de 2 tareas — visible hasta completar KYC + preguntas */}
                <OnboardingBanner onAction={() => setShowKycModal(true)} />

                {/* Stats Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="kpi-tile animate-slide-up-fade" style={{ animationDelay: '40ms' }}>
                    <div className="flex items-center justify-between relative">
                      <div className="space-y-1">
                        <p className="text-xs uppercase tracking-wider text-muted-foreground">{t("active_projects")}</p>
                        <p className="text-3xl font-bold tabular-nums">{projects.filter(p => p.status === "active").length}</p>
                        <p className="text-xs text-success flex items-center gap-1">
                          <TrendingUp className="w-3 h-3" /> +2 {t("this_month")}
                        </p>
                      </div>
                      <div className="h-12 w-12 rounded-2xl bg-success/12 text-success ring-1 ring-success/20 flex items-center justify-center">
                        <Building2 className="h-6 w-6" />
                      </div>
                    </div>
                  </div>

                  <div className="kpi-tile animate-slide-up-fade" style={{ animationDelay: '90ms' }}>
                    <div className="flex items-center justify-between relative">
                      <div className="space-y-1">
                        <p className="text-xs uppercase tracking-wider text-muted-foreground">{t("total_invested")}</p>
                        <p className="text-3xl font-bold tabular-nums">${totalInvested.toLocaleString()}</p>
                        <p className="text-xs text-info flex items-center gap-1">
                          <Activity className="w-3 h-3" /> {t("in_projects")}
                        </p>
                      </div>
                      <div className="h-12 w-12 rounded-2xl bg-info/12 text-info ring-1 ring-info/20 flex items-center justify-center">
                        <DollarSign className="h-6 w-6" />
                      </div>
                    </div>
                  </div>

                  <div className="kpi-tile animate-slide-up-fade" style={{ animationDelay: '140ms' }}>
                    <div className="flex items-center justify-between relative">
                      <div className="space-y-1">
                        <p className="text-xs uppercase tracking-wider text-muted-foreground">{t("current_value")}</p>
                        <p className="text-3xl font-bold tabular-nums">${totalCurrentValue.toLocaleString()}</p>
                        <p className="text-xs text-primary flex items-center gap-1">
                          <LineChart className="w-3 h-3" /> {t("updated_today")}
                        </p>
                      </div>
                      <div className="h-12 w-12 rounded-2xl bg-primary/12 text-primary ring-1 ring-primary/20 flex items-center justify-center">
                        <BarChart3 className="h-6 w-6" />
                      </div>
                    </div>
                  </div>

                  <div className="kpi-tile animate-slide-up-fade" style={{ animationDelay: '190ms' }}>
                    <div className="flex items-center justify-between relative">
                      <div className="space-y-1">
                        <p className="text-xs uppercase tracking-wider text-muted-foreground">{t("earnings")}</p>
                        <p className="text-3xl font-bold text-success tabular-nums">+${totalEarnings.toLocaleString()}</p>
                        <p className="text-xs text-brand-accent flex items-center gap-1">
                          <Award className="w-3 h-3" /> +{earningsPercent}% ROI
                        </p>
                      </div>
                      <div className="h-12 w-12 rounded-2xl bg-brand-accent/12 text-brand-accent ring-1 ring-brand-accent/25 flex items-center justify-center">
                        <TrendingUp className="h-6 w-6" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Portfolio Valuation */}
                <PortfolioValuation
                  totalInvested={totalInvested}
                  currentValue={totalCurrentValue}
                  totalEarnings={totalEarnings}
                  earningsPercent={earningsPercent}
                  assets={displayPortfolio.length > 0
                    ? displayPortfolio
                        .map((p: any) => ({
                          name: p.projectName || p.projectSymbol || 'Activo',
                          value: parseFloat(p.totalInvestedUsd || '0') || 0,
                        }))
                        .filter((a) => a.value > 0)
                    : undefined}
                />

                {/* Search and Filters */}
                <Card className="border-border/40">
                  <CardContent className="p-4">
                    <div className="flex flex-col lg:flex-row gap-4">
                      <div className="relative flex-1">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder={t("search_placeholder")}
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-11 h-11 bg-muted/50 border-0 focus-visible:ring-1 focus-visible:ring-primary"
                        />
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant={filterStatus === "all" ? "default" : "outline"}
                          size="sm"
                          onClick={() => setFilterStatus("all")}
                          className="h-11 px-4"
                        >
                          {t("all")}
                        </Button>
                        <Button
                          variant={filterStatus === "active" ? "default" : "outline"}
                          size="sm"
                          onClick={() => setFilterStatus("active")}
                          className="h-11 px-4 gap-1.5"
                        >
                          <Zap className="w-4 h-4" /> {t("active")}
                        </Button>
                        <Button
                          variant={filterStatus === "funded" ? "default" : "outline"}
                          size="sm"
                          onClick={() => setFilterStatus("funded")}
                          className="h-11 px-4 gap-1.5"
                        >
                          <CheckCircle2 className="w-4 h-4" /> {t("funded")}
                        </Button>
                        <div className="flex border rounded-lg overflow-hidden">
                          <Button
                            variant={viewMode === "grid" ? "secondary" : "ghost"}
                            size="icon"
                            className="h-11 w-11 rounded-none"
                            onClick={() => setViewMode("grid")}
                          >
                            <Grid className="h-4 w-4" />
                          </Button>
                          <Button
                            variant={viewMode === "list" ? "secondary" : "ghost"}
                            size="icon"
                            className="h-11 w-11 rounded-none"
                            onClick={() => setViewMode("list")}
                          >
                            <List className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Featured Projects — ver SHOW_FEATURED_PROJECTS arriba. */}
                {SHOW_FEATURED_PROJECTS && filterStatus === "all" && searchQuery === "" && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <Zap className="w-5 h-5 text-amber-500" />
                      <h3 className="text-lg font-semibold">{t("featured_projects")}</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {projects.filter(p => 'featured' in p ? p.featured : true).slice(0, 3).map((project) => (
                        <Card
                          key={project.id}
                          onClick={() => router.push(`/dashboard/user/projects/${project.id}`)}
                          className="group relative overflow-hidden border-border/40 hover:border-primary/50 transition-all duration-500 hover:shadow-2xl hover:shadow-primary/10 cursor-pointer"
                        >
                          <div className="absolute top-4 right-4 z-10">
                            <Badge className="bg-amber-500/90 text-foreground border-0 gap-1">
                              <Zap className="w-3 h-3" /> Destacado
                            </Badge>
                          </div>
                          <div className="relative h-52 overflow-hidden">
                            <div
                              className="absolute inset-0 bg-cover bg-center transform group-hover:scale-110 transition-transform duration-700"
                              style={{
                                backgroundImage: `url(${project.image})`,
                                backgroundColor: "hsl(var(--muted))",
                              }}
                            />
                            <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                              <span className="inline-flex items-center gap-1.5 rounded-full bg-background/70 px-2.5 py-1 text-xs font-medium text-foreground backdrop-blur-md ring-1 ring-border/40">
                                <MapPin className="w-3.5 h-3.5" />
                                {project.location}
                              </span>
                              {project.daysLeft > 0 && (
                                <Badge variant="outline" className="bg-background/70 backdrop-blur-md border-border/40">
                                  <Clock className="w-3 h-3 mr-1" /> {project.daysLeft} días
                                </Badge>
                              )}
                            </div>
                          </div>

                          <CardContent className="p-6 space-y-4">
                            <div className="space-y-1">
                              <h3 className="text-xl font-bold group-hover:text-primary transition-colors">
                                {project.name}
                              </h3>
                              <Badge variant="outline" className="text-xs">
                                {project.propertyType}
                              </Badge>
                            </div>

                            <div className="space-y-2">
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Progreso</span>
                                <span className="font-semibold text-primary">
                                  {project.raisedAmount != null && project.targetAmount != null && project.targetAmount > 0
                                    ? ((project.raisedAmount / project.targetAmount) * 100).toFixed(0)
                                    : 0}%
                                </span>
                              </div>
                              <div className="relative h-2 bg-muted rounded-full overflow-hidden">
                                <div 
                                  className="absolute inset-y-0 left-0 bg-gradient-to-r from-primary to-primary/70 rounded-full transition-all duration-500"
                                  style={{ 
                                    width: `${project.raisedAmount != null && project.targetAmount != null && project.targetAmount > 0 
                                      ? (project.raisedAmount / project.targetAmount) * 100 
                                      : 0}%` 
                                  }}
                                />
                              </div>
                              <div className="flex justify-between text-xs text-muted-foreground">
                                <span>${project.raisedAmount != null ? project.raisedAmount.toLocaleString() : "N/A"}</span>
                                <span>${project.targetAmount != null ? project.targetAmount.toLocaleString() : "N/A"}</span>
                              </div>
                            </div>

                            <div className="grid grid-cols-3 gap-4 pt-2">
                              <div className="text-center">
                                <p className="text-lg font-bold text-primary">{project.expectedReturn || "N/A"}</p>
                                <p className="text-xs text-muted-foreground">{t("return")}</p>
                              </div>
                              <div className="text-center">
                                <p className="text-lg font-bold">${project.tokenPrice != null ? project.tokenPrice : "N/A"}</p>
                                <p className="text-xs text-muted-foreground">{t("min")}</p>
                              </div>
                              <div className="text-center">
                                <p className="text-lg font-bold">{project.investorCount != null ? project.investorCount : 0}</p>
                                <p className="text-xs text-muted-foreground">{t("investors")}</p>
                              </div>
                            </div>

                            <Button 
                              className="w-full h-11 gap-2 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70"
                              onClick={(e) => {
                                e.stopPropagation();
                                router.push(`/dashboard/user/projects/${project.id}`);
                              }}
                            >
                              {t("invest_now")}
                              <ChevronRight className="w-4 h-4" />
                            </Button>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {/* All Projects */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-primary" />
                      <h3 className="text-lg font-semibold">
                        {filterStatus === "all" ? t("all_projects") : filterStatus === "active" ? t("active_projects_title") : t("funded_projects")}
                      </h3>
                      <Badge variant="secondary">{filteredProjects.length}</Badge>
                    </div>
                  </div>
                  
                  <div className={viewMode === "grid" 
                    ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-6" 
                    : "space-y-4"
                  }>
                    {/* Skeleton Loading State */}
                    {isLoadingProjects && (
                      <>
                        {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                          <Card key={i} className="overflow-hidden border-border/40">
                            <Skeleton className="h-52 w-full" />
                            <CardContent className="p-5 space-y-4">
                              <div className="space-y-2">
                                <Skeleton className="h-5 w-3/4" />
                                <Skeleton className="h-4 w-1/2" />
                              </div>
                              <div className="space-y-2">
                                <Skeleton className="h-4 w-full" />
                                <Skeleton className="h-2 w-full rounded-full" />
                              </div>
                              <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1">
                                  <Skeleton className="h-3 w-12" />
                                  <Skeleton className="h-4 w-20" />
                                </div>
                                <div className="space-y-1">
                                  <Skeleton className="h-3 w-12" />
                                  <Skeleton className="h-4 w-20" />
                                </div>
                              </div>
                              <Skeleton className="h-11 w-full rounded-md" />
                            </CardContent>
                          </Card>
                        ))}
                      </>
                    )}
                    
                    {/* Empty State */}
                    {!isLoadingProjects && filteredProjects.length === 0 && (
                      <div className="col-span-full flex flex-col items-center justify-center py-16 text-center">
                        <Building2 className="w-16 h-16 text-muted-foreground/50 mb-4" />
                        <h3 className="text-lg font-semibold text-muted-foreground mb-2">
                          {t("no_projects")}
                        </h3>
                        <p className="text-sm text-muted-foreground/70 max-w-md">
                          {filterStatus !== "all" 
                            ? `${t("no_projects_filter")} "${filterStatus === "active" ? t("active") : t("funded")}" ${t("at_this_time")}`
                            : t("working_on_new")
                          }
                        </p>
                        {filterStatus !== "all" && (
                          <Button 
                            variant="outline" 
                            className="mt-4"
                            onClick={() => setFilterStatus("all")}
                          >
                            {t("show_all")}
                          </Button>
                        )}
                      </div>
                    )}
                    
                    {/* Projects Grid */}
                    {!isLoadingProjects && filteredProjects.length > 0 && filteredProjects.map((project) => (
                      <Card
                        key={project.id}
                        onClick={() => router.push(`/dashboard/user/projects/${project.id}`)}
                        className={`group overflow-hidden border-border/40 hover:border-primary/50 transition-all duration-300 hover:shadow-lg hover:shadow-primary/5 cursor-pointer ${
                          viewMode === "list" ? "flex flex-row" : ""
                        }`}
                      >
                        <div className={`relative overflow-hidden ${viewMode === "list" ? "w-64 flex-shrink-0" : "h-52"}`}>
                          <div
                            className="absolute inset-0 bg-cover bg-center transform group-hover:scale-105 transition-transform duration-500"
                            style={{
                              backgroundImage: `url(${project.image})`,
                              backgroundColor: "hsl(var(--muted))",
                            }}
                          />
                          <div className="absolute top-3 left-3">{getStatusBadge(project)}</div>
                        </div>

                        <CardContent className={`p-5 space-y-4 ${viewMode === "list" ? "flex-1 flex flex-col justify-between" : ""}`}>
                          <div className="space-y-2">
                            <div className="flex items-start justify-between">
                              <div>
                                <h3 className="font-bold text-foreground group-hover:text-primary transition-colors">
                                  {project.name}
                                </h3>
                                <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                                  <MapPin className="w-3 h-3" /> {project.location}
                                </p>
                              </div>
                              <Badge variant="outline" className="text-[10px] shrink-0">
                                {project.propertyType}
                              </Badge>
                            </div>

                            {project.status !== "coming_soon" && (
                              <div className="space-y-1">
                                <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-primary rounded-full"
                                    style={{ 
                                      width: `${project.raisedAmount != null && project.targetAmount != null && project.targetAmount > 0 
                                        ? (project.raisedAmount / project.targetAmount) * 100 
                                        : 0}%` 
                                    }}
                                  />
                                </div>
                                <div className="flex justify-between text-[10px] text-muted-foreground">
                                  <span>
                                    {project.raisedAmount != null && project.targetAmount != null && project.targetAmount > 0 
                                      ? ((project.raisedAmount / project.targetAmount) * 100).toFixed(0) 
                                      : 0}% {te("funded")}
                                  </span>
                                  <span>
                                    ${project.targetAmount != null && project.raisedAmount != null 
                                      ? (project.targetAmount - project.raisedAmount).toLocaleString() 
                                      : "N/A"} {te("remaining")}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="flex items-center justify-between pt-2 border-t border-border/40">
                            <div className="flex gap-4 text-xs">
                              <div>
                                <span className="text-primary font-semibold">{project.expectedReturn || "N/A"}</span>
                                <span className="text-muted-foreground ml-1">{t("return")}</span>
                              </div>
                              <div>
                                <span className="font-semibold">${project.tokenPrice != null ? project.tokenPrice : "N/A"}</span>
                                <span className="text-muted-foreground ml-1">{t("min")}</span>
                              </div>
                            </div>
                            {(() => {
                              // El CTA se comporta según el estado + los flags on-chain de venta:
                              // sólo se puede invertir si la venta está activa y el proyecto no está
                              // financiado ni "próximamente".
                              const paused = project.status === "active" && project.isSaleActive === false;
                              const disabled =
                                project.status === "coming_soon" || project.status === "funded" || paused;
                              const label =
                                project.status === "coming_soon"
                                  ? te("soon")
                                  : project.status === "funded"
                                    ? te("fully_funded")
                                    : paused
                                      ? te("sale_paused")
                                      : te("invest");
                              return (
                                <Button
                                  size="sm"
                                  className={disabled ? "bg-muted text-muted-foreground" : ""}
                                  disabled={disabled}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleInvestClick(project.id);
                                  }}
                                >
                                  {label}
                                </Button>
                              );
                            })()}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Portfolio Section */}
            {activeSection === "portfolio" && (
              <motion.div
                key="portfolio"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="space-y-8"
                {...paso('cartera')}
              >
                {/* Portfolio Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card className="border-border/40 bg-gradient-to-br from-emerald-500/5 to-transparent">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3">
                        <div className="h-12 w-12 rounded-2xl bg-emerald-500/10 flex items-center justify-center">
                          <DollarSign className="h-6 w-6 text-emerald-500" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">{te("total_invested")}</p>
                          <p className="text-2xl font-bold">${(portfolio.length > 0 ? parseFloat(portfolioTotalInvested) : totalInvested).toLocaleString()}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border-border/40 bg-gradient-to-br from-sky-500/5 to-transparent">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3">
                        <div className="h-12 w-12 rounded-2xl bg-sky-500/10 flex items-center justify-center">
                          <BarChart3 className="h-6 w-6 text-sky-500" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">{te("current_value")}</p>
                          <p className="text-2xl font-bold">${totalCurrentValue.toLocaleString()}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border-border/40 bg-gradient-to-br from-violet-500/5 to-transparent">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3">
                        <div className="h-12 w-12 rounded-2xl bg-violet-500/10 flex items-center justify-center">
                          <TrendingUp className="h-6 w-6 text-violet-500" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">{te("earnings")}</p>
                          <p className="text-2xl font-bold text-emerald-500">+${totalEarnings.toLocaleString()}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border-border/40 bg-gradient-to-br from-amber-500/5 to-transparent">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3">
                        <div className="h-12 w-12 rounded-2xl bg-amber-500/10 flex items-center justify-center">
                          <Award className="h-6 w-6 text-amber-500" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">{te("average_roi")}</p>
                          <p className="text-2xl font-bold text-emerald-500">+{earningsPercent}%</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Valorización del portafolio + asignación por propiedad */}
                <PortfolioValuation
                  totalInvested={portfolio.length > 0 ? parseFloat(portfolioTotalInvested) : totalInvested}
                  currentValue={totalCurrentValue}
                  totalEarnings={totalEarnings}
                  earningsPercent={earningsPercent}
                  assets={displayPortfolio.length > 0
                    ? displayPortfolio
                        .map((p: any) => ({
                          name: p.projectName || p.projectSymbol || 'Activo',
                          value: p.currentValue ?? (parseFloat(p.totalInvestedUsd || '0') || 0),
                        }))
                        .filter((a: any) => a.value > 0)
                    : undefined}
                />

                {/* Inversiones — una tarjeta por propiedad con su valorización */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <PieChart className="w-5 h-5 text-primary" />
                    <h3 className="text-lg font-semibold">{te("my_active_investments")}</h3>
                    <Badge variant="secondary">{displayPortfolio.length}</Badge>
                  </div>
                  {isLoadingPortfolio ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                      {[1, 2, 3].map((i) => (
                        <Card key={i} className="overflow-hidden border-border/40">
                          <Skeleton className="h-28 w-full" />
                          <CardContent className="p-4 space-y-3">
                            <Skeleton className="h-4 w-3/4" />
                            <Skeleton className="h-6 w-1/2" />
                            <Skeleton className="h-9 w-full" />
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : displayPortfolio.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                      {displayPortfolio.map((investment: any, idx: number) => (
                        <PropertyHoldingCard
                          key={investment.projectId || idx}
                          holding={investment}
                          index={idx}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="py-12 text-center text-muted-foreground">
                      <PieChart className="h-12 w-12 mx-auto mb-3 text-muted-foreground/30" />
                      <p>No tienes inversiones activas aún.</p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Balances Section */}
            {activeSection === "balances" && (
              <motion.div
                key="balances"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="space-y-8"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                   <Card className="relative overflow-hidden border border-yellow-100 shadow-none bg-background h-full">
                    {/* Background decoration */}
                    <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-gradient-to-br from-yellow-100/40 to-yellow-50/30 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 opacity-100 pointer-events-none" />
                    <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-gradient-to-tr from-yellow-50/30 to-transparent rounded-full blur-3xl translate-y-1/3 -translate-x-1/3 pointer-events-none" />
                    
                    <CardContent className="relative p-8 flex flex-col justify-between h-full">
                      <div className="space-y-8">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-yellow-700/80 bg-yellow-50/50 backdrop-blur-sm border border-yellow-100 px-3 py-1.5 rounded-full">
                             <Wallet className="h-3.5 w-3.5" />
                             <span className="text-xs font-bold uppercase tracking-wider">{te("available_balance")}</span>
                          </div>
                          <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 hover:bg-yellow-200 border-yellow-200 px-2.5 py-1">
                             Base Sepolia
                          </Badge>
                        </div>
                        
                           <div className="flex flex-col gap-1">
                              {isLoadingBalance ? (
                                <div className="space-y-2">
                                  <Skeleton className="h-12 w-48 bg-muted/50" />
                                  <Skeleton className="h-4 w-32 bg-muted/50" />
                                </div>
                              ) : (
                                <>
                                   <div className="flex items-baseline gap-1">
                                      <span className="text-5xl md:text-6xl font-bold text-foreground tracking-tighter">
                                         {parseFloat(usdcBalance).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                                      </span>
                                      <span className="text-2xl font-bold text-muted-foreground ml-2">USD</span>
                                   </div>
                                   <p className="text-sm font-medium text-muted-foreground mt-2 pl-1 flex items-center gap-2">
                                      <span>≈ {parseFloat(usdcBalance).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} USDC</span>
                                      <span className="h-1 w-1 rounded-full bg-muted"></span>
                                      <span className="text-emerald-600 flex items-center gap-0.5">
                                         <TrendingUp className="w-3 h-3" /> 0.0%
                                      </span>
                                   </p>
                                </>
                              )}
                           </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mt-8">
                        <Button
                          className="h-12 rounded-lg text-base font-bold bg-primary hover:bg-primary/90 text-primary-foreground shadow-md transition-all active:scale-[0.98]"
                          onClick={() => setShowDepositModal(true)}
                        >
                          <ArrowDownRight className="w-5 h-5 mr-2" />
                          {te("deposit")}
                        </Button>
                        <Button
                          variant="outline"
                          className="h-12 rounded-lg text-base font-bold border-2 border-primary text-foreground hover:bg-primary/10 transition-all active:scale-[0.98] bg-background"
                          onClick={() => setActiveSection("withdrawals")}
                        >
                          <ArrowUpRight className="w-5 h-5 mr-2" />
                          {te("withdraw")}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="relative overflow-hidden border-border/40">
                    <div className="absolute inset-0 bg-gradient-to-br from-violet-500/10 via-violet-500/5 to-transparent" />
                    <CardHeader className="relative pb-2">
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <div className="h-10 w-10 rounded-xl bg-violet-500/10 flex items-center justify-center">
                          <Building2 className="h-5 w-5 text-violet-500" />
                        </div>
                        Mis Propiedades (Tokens)
                      </CardTitle>
                      <CardDescription>Portafolio de participaciones inmobiliarias</CardDescription>
                    </CardHeader>
                    <CardContent className="relative">
                      <div className="space-y-3 mt-2">
                      <div className="space-y-3 mt-2">
                        {isLoadingPortfolio ? (
                          <div className="space-y-3">
                            {[1, 2, 3].map((i) => (
                              <div key={i} className="flex items-center justify-between p-3 bg-background/40 rounded-xl border border-border/20">
                                <div className="flex items-center gap-3">
                                  <Skeleton className="h-10 w-10 rounded-lg" />
                                  <div className="space-y-1">
                                     <Skeleton className="h-4 w-28" />
                                     <Skeleton className="h-3 w-16" />
                                  </div>
                                </div>
                                <div className="space-y-1">
                                   <Skeleton className="h-4 w-16 ml-auto" />
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : displayPortfolio.length > 0 ? (
                          displayPortfolio.map((token: any, i: number) => (
                           <div key={i} className="flex items-center justify-between p-3 bg-background/40 rounded-xl border border-border/20 hover:bg-background/60 transition-colors">
                              <div className="flex items-center gap-3">
                                 <div className="relative h-10 w-10 rounded-lg overflow-hidden border border-border/10 shadow-sm shrink-0">
                                   {token.coverImage ? (
                                     <img 
                                       src={token.coverImage} 
                                       alt={token.projectName}

                                       className="object-cover"
                                     />
                                   ) : (
                                     <div className="w-full h-full bg-muted flex items-center justify-center">
                                       <Building2 className="w-5 h-5 text-muted-foreground/50" />
                                     </div>
                                   )}
                                 </div>
                                 <div className="overflow-hidden">
                                    <p className="font-semibold text-sm truncate">{token.projectName}</p>
                                    <p className="text-xs text-muted-foreground">{token.realBalance ? parseFloat(token.realBalance).toLocaleString() : '0'} {token.projectSymbol}</p>
                                 </div>
                              </div>
                              <div className="text-right shrink-0">
                                 <p className="font-bold text-sm">${token.totalInvestedUsd ? parseFloat(token.totalInvestedUsd).toLocaleString() : '0'}</p>
                                 {token.earningsPercent != null && (
                                   <p className="text-xs text-emerald-500">+{token.earningsPercent}%</p>
                                 )}
                              </div>
                           </div>
                         ))
                        ) : (
                          <div className="py-8 text-center text-sm text-muted-foreground">No tienes propiedades aún.</div>
                        )}
                      </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Rendimientos del mes */}
                <WalletYieldSummary />

                {/* Wallet CDP */}
                <WalletCard kycStatus={kycStatus} />
                
                {/* Recent Transactions Snippet */}
                <Card className="border-border/40">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                       <History className="w-4 h-4 text-primary" />
                       Actividad Reciente
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {isLoadingTransactions ? (
                      <div className="py-8 text-center text-muted-foreground">Cargando...</div>
                    ) : displayTransactions.length > 0 ? (
                      <div className="space-y-3">
                         {displayTransactions.slice(0, 3).map((tx: any, idx: number) => (
                           <div key={idx} className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                             <div className="flex items-center gap-3">
                               <div className={`relative flex items-center justify-center h-10 w-10 shrink-0 overflow-hidden rounded-xl ${tx.project?.image ? 'border border-border/10 shadow-sm' : (tx.type === 'deposit' ? 'bg-green-500/10 text-green-500' : 'bg-blue-500/10 text-blue-500')}`}>
                                  {tx.project?.image ? (
                                    <img 
                                      src={tx.project.image} 
                                      alt={tx.project.name} 

                                      className="object-cover"
                                    />
                                  ) : (
                                    tx.type === 'deposit' ? <ArrowDownRight className="w-5 h-5" /> : <ArrowUpRight className="w-5 h-5" />
                                  )}
                               </div>
                               <div>
                                  <p className="font-medium text-sm capitalize">
                                    {tx.type === 'investment' && tx.project ? `Inversión: ${tx.project.name}`
                                      : tx.type === 'dividend' && tx.project ? `Rendimiento: ${tx.project.name}`
                                      : (tx.type || 'Transacción')}
                                  </p>
                                  <p className="text-xs text-muted-foreground">{new Date(tx.date || Date.now()).toLocaleDateString()}</p>
                               </div>
                             </div>
                             <div className="text-right">
                                <p className="font-bold text-sm">{tx.amountUsd ? `${parseFloat(tx.amountUsd).toLocaleString()} USD` : tx.amount ? `${tx.amount} USD` : ''}</p>
                                <p className="text-xs text-muted-foreground capitalize">{tx.status}</p>
                             </div>
                           </div>
                         ))}
                         <Button variant="ghost" className="w-full text-xs" onClick={() => setActiveSection("history")}>
                            Ver todo el historial
                         </Button>
                      </div>
                    ) : (
                      <div className="py-8 text-center text-muted-foreground text-sm">No hay transacciones recientes</div>
                    )}
                  </CardContent>
                </Card>

                {pendingWithdrawals > 0 && (
                  <Card className="border-amber-500/30 bg-amber-500/5">
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className="h-10 w-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                        <Clock className="h-5 w-5 text-amber-500" />
                      </div>
                      <div>
                        <p className="font-semibold">{te("pending_withdrawal")}</p>
                        <p className="text-sm text-muted-foreground">
                          ${pendingWithdrawals.toLocaleString()} {te("processing_estimated")}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </motion.div>
            )}

            {/* Profile Section */}
            {activeSection === "profile" && (
              <motion.div
                key="profile"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="space-y-8"
              >
                {/* Cabecera: identidad + patrimonio de un vistazo */}
                <ProfileHero
                  name={session?.user?.name || "Usuario"}
                  email={session?.user?.email || ""}
                  kycVerified={kycStatus === "VERIFIED"}
                  memberSince={new Date().getFullYear()}
                  currentValue={totalCurrentValue}
                  invested={totalInvested}
                  earnings={totalEarnings}
                  roi={earningsPercent}
                  properties={displayPortfolio.length}
                  onEdit={() => setShowEditProfile(true)}
                />

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                  {/* Progreso accionable de la cuenta */}
                  <ProfileCompletion
                    kycVerified={kycStatus === "VERIFIED"}
                    hasWallet={Boolean(evmAddress) || kycStatus === "VERIFIED"}
                    hasInvestments={displayPortfolio.length > 0}
                    onVerifyKyc={() => setShowKycFlow(true)}
                    onOpenWallet={() => setActiveSection("balances")}
                    onExplore={() => setActiveSection("marketplace")}
                  />

                  {/* Datos de la cuenta (lo técnico, plegado) */}
                  <AccountDetails
                    name={session?.user?.name || "Usuario"}
                    email={session?.user?.email || ""}
                    memberSince={String(new Date().getFullYear())}
                    userId={session?.user?.id || ""}
                  />
                </div>

                {/* Logros del inversor */}
                <ProfileAchievements
                  properties={displayPortfolio.length}
                  countries={new Set(displayPortfolio.map((p: any) => (p.location || "").split(",").pop()?.trim()).filter(Boolean)).size}
                  earnings={totalEarnings}
                  invested={totalInvested}
                />

                {/* Preguntas de seguridad (para pedir desbloqueo de cuenta) */}
                <SecurityQuestionsCard />

                {/* KYC: solo cuando ya está verificado (estado) o cuando el usuario
                    pide verificarse desde el checklist. Evita repetir el mismo CTA. */}
                {(kycStatus === "VERIFIED" || showKycFlow) && (
                  <KycVerification onStatusChange={setKycStatus} />
                )}

                {/* Wallet: solo tiene sentido una vez verificado; si no, el
                    checklist ya explica que se crea al completar el KYC. */}
                {kycStatus === "VERIFIED" && <WalletCard kycStatus={kycStatus} />}

                {/* Edición real del perfil */}
                <EditProfileModal
                  open={showEditProfile}
                  name={session?.user?.name || ""}
                  email={session?.user?.email || ""}
                  country={session?.user?.country}
                  onClose={() => setShowEditProfile(false)}
                />
              </motion.div>
            )}

            {/* Withdrawals Section */}
            {activeSection === "withdrawals" && (
              <motion.div
                key="withdrawals"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-8"
              >
                {/* Solicitud de Retiro */}
                <Card className="border-border/40 h-fit">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <ArrowUpRight className="w-5 h-5 text-primary" />
                      {te("request_withdrawal")}
                    </CardTitle>
                    <CardDescription>{te("withdraw_desc")}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <WithdrawalForm 
                      availableBalance={availableBalance} 
                      onSuccess={() => setWithdrawalsRefreshKey(prev => prev + 1)}
                    />
                  </CardContent>
                </Card>

                {/* Historial */}
                <Card className="border-border/40 h-fit">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                       <History className="w-5 h-5 text-primary" />
                       {tw("history_title")}
                    </CardTitle>
                    <CardDescription>{tw("history_subtitle")}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <WithdrawalList refreshTrigger={withdrawalsRefreshKey} />
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Dividends Section — sólo en modo demo */}
            {activeSection === "dividends" && features.demoMode && (
              <motion.div
                key="dividends"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <DividendsSection
                  reinvestProjects={projects.map((p) => ({ id: p.id, name: p.name }))}
                />
              </motion.div>
            )}

            {/* History Section */}
            {activeSection === "history" && (
              <motion.div
                key="history"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="space-y-6"
              >
                <Card className="border-border/40">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                       <History className="w-5 h-5 text-primary" />
                       Historial de Transacciones
                    </CardTitle>
                    <CardDescription>Movimientos de tu wallet y plataforma</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {isLoadingTransactions ? (
                      <div className="py-12 flex justify-center">
                        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
                      </div>
                    ) : displayTransactions.length > 0 ? (
                      <div className="space-y-2">
                        {displayTransactions.map((tx: any, idx: number) => (
                          <div key={idx} className="flex flex-col md:flex-row md:items-center justify-between p-4 bg-muted/20 hover:bg-muted/40 rounded-xl border border-border/40 transition-colors gap-4">
                            <div className="flex items-center gap-4">
                               <div className={`relative flex items-center justify-center h-12 w-12 shrink-0 overflow-hidden rounded-xl ${tx.project?.image ? 'border border-border/10 shadow-sm' : (tx.type === 'deposit' ? 'bg-green-500/10 text-green-500' : tx.type === 'investment' ? 'bg-violet-500/10 text-violet-500' : 'bg-blue-500/10 text-blue-500')}`}>
                                  {tx.project?.image ? (
                                     <img 
                                      src={tx.project.image} 
                                      alt={tx.project.name} 

                                      className="object-cover"
                                     />
                                  ) : (
                                     tx.type === 'deposit' ? <ArrowDownRight className="w-5 h-5" /> : 
                                     tx.type === 'investment' ? <Zap className="w-5 h-5" /> :
                                     <ArrowUpRight className="w-5 h-5" />
                                  )}
                               </div>
                               <div>
                                  <p className="font-semibold capitalize">
                                    {tx.type === 'investment' ? 'Inversión Realizada'
                                      : tx.type === 'dividend' ? 'Rendimiento'
                                      : tx.type === 'deposit' ? 'Depósito'
                                      : tx.type === 'withdrawal' ? 'Retiro'
                                      : tx.type}
                                    {tx.project && <span className="text-muted-foreground font-normal"> • {tx.project.name}</span>}
                                  </p>
                                  <p className="text-xs text-muted-foreground flex gap-2">
                                     <span>{new Date(tx.date || Date.now()).toLocaleString()}</span>
                                     {tx.txHash && <span>• {tx.txHash.slice(0,6)}...{tx.txHash.slice(-4)}</span>}
                                  </p>
                               </div>
                            </div>
                            <div className="flex items-center justify-between md:justify-end gap-4 w-full md:w-auto">
                               <div className="text-right">
                                  <p className={`font-bold ${tx.type === 'deposit' || tx.type === 'dividend' ? 'text-emerald-500' : 'text-foreground'}`}>
                                    {tx.type === 'deposit' || tx.type === 'dividend' ? '+' : '-'}{tx.amountUsd ? parseFloat(tx.amountUsd).toLocaleString() : tx.amount} USD
                                  </p>
                                  <Badge variant="outline" className="text-[10px] capitalize">{tx.status}</Badge>
                               </div>
                               {tx.txHash && (
                                 <Button variant="ghost" size="icon" asChild>
                                    <a href={`https://basescan.org/tx/${tx.txHash}`} target="_blank" rel="noopener noreferrer">
                                       <ExternalLink className="w-4 h-4 text-muted-foreground" />
                                    </a>
                                 </Button>
                               )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                       <div className="py-16 text-center">
                         <div className="mx-auto w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                            <History className="h-8 w-8 text-muted-foreground/50" />
                         </div>
                         <h3 className="text-lg font-medium">No hay transacciones aún</h3>
                         <p className="text-muted-foreground">Tus movimientos aparecerán aquí.</p>
                       </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Settings Section */}
            {activeSection === "settings" && (
              <motion.div
                key="settings"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="space-y-6"
              >
                <Card className="border-border/40">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                       <Settings className="w-5 h-5 text-primary" />
                       {te("settings_title")}
                    </CardTitle>
                    <CardDescription>{te("settings_desc")}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* General Preferences */}
                    <div className="space-y-4">
                      <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">{te("preferences")}</h4>
                      
                      {/* Language */}
                      <div className="flex items-center justify-between p-4 rounded-xl bg-muted/30 border border-border/40">
                        <div className="space-y-1">
                          <Label className="text-base">{te("language")}</Label>
                          <p className="text-sm text-muted-foreground">{te("select_language")}</p>
                        </div>
                        <LanguageSwitcher />
                      </div>

                      {/* Currency (Mock) */}
                      <div className="flex items-center justify-between p-4 rounded-xl bg-muted/30 border border-border/40">
                         <div className="space-y-1">
                          <Label className="text-base">{te("currency")}</Label>
                          <p className="text-sm text-muted-foreground">{te("display_currency")}</p>
                        </div>
                        <Badge variant="outline" className="h-9 px-4 text-sm font-normal">
                          USD ($)
                        </Badge>
                      </div>
                    </div>

                    <Separator />

                    {/* Notifications (Mock) */}
                    <div className="space-y-4">
                       <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">{te("notifications")}</h4>
                       
                       <div className="flex items-center justify-between p-4 rounded-xl bg-muted/30 border border-border/40">
                        <div className="space-y-1">
                          <Label className="text-base">{te("email_notifications")}</Label>
                          <p className="text-sm text-muted-foreground">{te("email_notifications_desc")}</p>
                        </div>
                        <Switch defaultChecked />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Help Section */}
            {activeSection === "help" && (
              <motion.div
                key="help"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <HelpSection />
              </motion.div>
            )}
          </AnimatePresence>
        </main>
      </SidebarInset>
      
      <DepositModal 
        isOpen={showDepositModal} 
        onClose={() => setShowDepositModal(false)} 
        walletAddress={evmAddress} 
      />
      
      <OnboardingRequiredModal
        isOpen={showKycModal}
        onClose={() => setShowKycModal(false)}
        onComplete={() => {
          checkKycStatus();
          setShowKycModal(false);
        }}/>

    </SidebarProvider>
    </NotificationsProvider>
  );
}

export const Route = createFileRoute('/dashboard/user/')({
  component: UserDashboard,
})
